// PsycheMatrix interstitial: the one paid-attention moment in the game.
// Design source: PsycheMatrix/remotion-ads/out/approved/wot-handoff.md.
//
// Rules, all of them here so the component stays dumb:
//   1. Master switch is the server flag pm_promotions_enabled (app_settings),
//      the same switch that gates every other PsycheMatrix surface.
//   2. One impression per player per calendar day.
//   3. Never inside a player's first three completed games: those decide
//      whether they stay, and nothing may compete with the game there.
//   4. Web dev/testing override: ?ad=1 on the URL shows it regardless.

export const PM_AD = {
  // 'question': the eleventh question (components/PmQuestion, lib/pmQuestion);
  // 'film': the NoiseBubbles interstitial below. Same gating either way.
  mode: 'question' as 'question' | 'film',
  creative: 'noise_v1',
  format: 'vertical',
  // Hosting is decided per build: production points at the CDN copy, the
  // dev server serves the file from public/ads (gitignored, never in the APK).
  src: (import.meta.env.VITE_PM_AD_URL as string | undefined) || '/ads/noise_v1.mp4',
  // Google Play's Better Ads policy: a full-screen interstitial must be
  // closable within 15 seconds. This is that ceiling, not a courtesy.
  skipAfterSeconds: 15,
  ctaAtSeconds: 61.2,
  durationSeconds: 65.4,
  ctaUrl: 'https://psychematrix.io/assessment?utm_source=wot&utm_medium=interstitial&utm_campaign=noise_v1',
  minGamesBeforeFirstAd: 3,
} as const;

const GAMES_KEY = 'wot_games_completed';
const LAST_AD_KEY = 'wot_pm_ad_last_day';

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Called once per finished match (Results mount). Returns the new total. */
export function recordGameCompleted(): number {
  try {
    const n = (parseInt(localStorage.getItem(GAMES_KEY) ?? '0', 10) || 0) + 1;
    localStorage.setItem(GAMES_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

/** The same count, read only. On Results it already includes the match on screen. */
export function gamesCompleted(): number {
  try {
    return parseInt(localStorage.getItem(GAMES_KEY) ?? '0', 10) || 0;
  } catch {
    return 0;
  }
}

// The router rewrites the URL before any screen mounts, so the flag has to
// be read the moment this module loads, then kept for the tab session.
try {
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ad') === '1') {
    sessionStorage.setItem('wot_ad_dev', '1');
  }
} catch { /* storage blocked */ }

export function devAdOverride(): boolean {
  try {
    return sessionStorage.getItem('wot_ad_dev') === '1';
  } catch {
    return false;
  }
}

/** Decide at Results mount. `gamesCompleted` is the count INCLUDING this match. */
export function shouldShowPmAd(pmEnabled: boolean, gamesCompleted: number): boolean {
  if (devAdOverride()) return true;
  if (!pmEnabled) return false;
  if (gamesCompleted <= PM_AD.minGamesBeforeFirstAd) return false;
  try {
    return localStorage.getItem(LAST_AD_KEY) !== todayKey();
  } catch {
    return false;
  }
}

export function markPmAdShown(): void {
  try { localStorage.setItem(LAST_AD_KEY, todayKey()); } catch { /* storage blocked */ }
}
